"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCurrentWeekInWindow = exports.getOneIndexedWeek = exports.getLatestDayjs = exports.getEarliestDayjs = exports.getUniqueDays = exports.getDayOfWeek = exports.formatToICSDate = exports.convertUtcToLocal = exports.convertTimeToTargetTimezone = exports.roundToNearestMinutes = exports.getDayjsInIntervals = exports.sDayJs = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
// Import plugins declarations - Needed to extend the Dayjs interface
// Without this, the Dayjs interface is not typesafe
// and /apps is not able to use the plugins
require("dayjs/plugin/advancedFormat");
require("dayjs/plugin/duration");
require("dayjs/plugin/isBetween");
require("dayjs/plugin/isoWeek");
require("dayjs/plugin/isSameOrAfter");
require("dayjs/plugin/isSameOrBefore");
require("dayjs/plugin/isToday");
require("dayjs/plugin/minMax");
require("dayjs/plugin/relativeTime");
require("dayjs/plugin/timezone");
require("dayjs/plugin/utc");
require("dayjs/plugin/weekOfYear");
// Import the plugins to extend the Dayjs interface
const advancedFormat_1 = __importDefault(require("dayjs/plugin/advancedFormat"));
const duration_1 = __importDefault(require("dayjs/plugin/duration"));
const isBetween_1 = __importDefault(require("dayjs/plugin/isBetween"));
const isoWeek_1 = __importDefault(require("dayjs/plugin/isoWeek"));
const isSameOrAfter_1 = __importDefault(require("dayjs/plugin/isSameOrAfter"));
const isSameOrBefore_1 = __importDefault(require("dayjs/plugin/isSameOrBefore"));
const isToday_1 = __importDefault(require("dayjs/plugin/isToday"));
const minMax_1 = __importDefault(require("dayjs/plugin/minMax"));
const relativeTime_1 = __importDefault(require("dayjs/plugin/relativeTime"));
const timezone_1 = __importDefault(require("dayjs/plugin/timezone"));
const utc_1 = __importDefault(require("dayjs/plugin/utc"));
const weekOfYear_1 = __importDefault(require("dayjs/plugin/weekOfYear"));
// Extend dayjs with plugins
dayjs_1.default.extend(advancedFormat_1.default);
dayjs_1.default.extend(duration_1.default);
dayjs_1.default.extend(isBetween_1.default);
dayjs_1.default.extend(isoWeek_1.default);
dayjs_1.default.extend(isSameOrAfter_1.default);
dayjs_1.default.extend(isSameOrBefore_1.default);
dayjs_1.default.extend(isToday_1.default);
dayjs_1.default.extend(minMax_1.default);
dayjs_1.default.extend(relativeTime_1.default);
dayjs_1.default.extend(timezone_1.default);
dayjs_1.default.extend(utc_1.default);
dayjs_1.default.extend(weekOfYear_1.default);
// Singleton for extended Day.js
exports.sDayJs = dayjs_1.default;
/**
 * Generate an array of Dayjs objects at regular intervals between two dates.
 * @param {Dayjs} start - The start date as a Dayjs object.
 * @param {Dayjs} end - The end date as a Dayjs object.
 * @param {number} intervalMinutes - The interval in minutes.
 * @return {Dayjs[]} - An array of Dayjs objects.
 */
const getDayjsInIntervals = (start, end, intervalMinutes) => {
    const intervals = [];
    let current = start.add(intervalMinutes, 'minute'); // Move to the first interval
    while (current.isBefore(end)) {
        intervals.push(current.clone());
        current = current.add(intervalMinutes, 'minute');
    }
    return intervals;
};
exports.getDayjsInIntervals = getDayjsInIntervals;
/**
 * Round a Dayjs object to the nearest interval.
 * @param {Dayjs} time - The Dayjs object.
 * @param {string} action - Either 'add' or 'subtract'.
 * @param {number} nearest - The nearest minute interval.
 * @return {Dayjs} - The rounded Dayjs object.
 */
const roundToNearestMinutes = (time, action, nearest) => {
    const minutes = time.minute();
    const roundedMinutes = action === 'add'
        ? Math.ceil(minutes / nearest) * nearest
        : Math.floor(minutes / nearest) * nearest;
    return time.startOf('hour').add(roundedMinutes, 'minute');
};
exports.roundToNearestMinutes = roundToNearestMinutes;
/**
 * Convert a time string from one timezone to another.
 * @param {string} time - The time string in 'HH:mm' format.
 * @param {string} originalTimezone - The original timezone.
 * @param {string} targetTimezone - The target timezone.
 * @return {string} - The converted time string.
 */
const convertTimeToTargetTimezone = (time, originalTimezone, targetTimezone) => {
    // Combine the time with a reference date
    const referenceDate = (0, exports.sDayJs)().format('YYYY-MM-DD');
    const dateTimeString = `${referenceDate} ${time}`;
    // Parse in the original timezone and convert to target timezone
    return exports.sDayJs
        .tz(dateTimeString, originalTimezone)
        .tz(targetTimezone)
        .format('HH:mm');
};
exports.convertTimeToTargetTimezone = convertTimeToTargetTimezone;
/**
 * Convert a UTC date string to a local date string.
 * @param {string} date - The UTC date string.
 * @param {string} timezone - The target timezone.
 * @return {Dayjs} - The converted Dayjs object.
 */
const convertUtcToLocal = (date, timezone) => {
    return exports.sDayJs.utc(date).tz(timezone);
};
exports.convertUtcToLocal = convertUtcToLocal;
/**
 * Format a timestamp to an ICS date string.
 * @param {number} timestamp - The timestamp.
 * @return {string} - The formatted ICS date string.
 */
const formatToICSDate = (timestamp) => {
    return exports.sDayJs.unix(timestamp).utc().format('YYYYMMDDTHHmmss[Z]');
};
exports.formatToICSDate = formatToICSDate;
/**
 * Get the day of the week from a Dayjs object.
 * @param {Dayjs} date - The Dayjs object.
 * @return {DayOfWeek} - The day of the week.
 */
const getDayOfWeek = (date) => {
    const day = date.day();
    if (day === 0)
        return 'sunday';
    if (day === 1)
        return 'monday';
    if (day === 2)
        return 'tuesday';
    if (day === 3)
        return 'wednesday';
    if (day === 4)
        return 'thursday';
    if (day === 5)
        return 'friday';
    return 'saturday';
};
exports.getDayOfWeek = getDayOfWeek;
/**
 * Get unique days from a list of Dayjs objects.
 * @param {Dayjs[]} dates - The list of Dayjs objects.
 * @return {number} - The number of unique days.
 */
const getUniqueDays = (dates) => {
    return [...new Set(dates.map((date) => date.format('YYYY-MM-DD')))].length;
};
exports.getUniqueDays = getUniqueDays;
/**
 * Get the earliest date from a list of Dayjs objects.
 * @param {Dayjs[]} dates - The list of Dayjs objects.
 * @return {Dayjs} - The earliest Dayjs object.
 */
const getEarliestDayjs = (dates) => {
    return exports.sDayJs.unix(Math.min(...dates.map((date) => date.unix())));
};
exports.getEarliestDayjs = getEarliestDayjs;
/**
 * Get the latest date from a list of Dayjs objects.
 * @param {Dayjs[]} dates - The list of Dayjs objects.
 * @return {Dayjs} - The latest Dayjs object.
 */
const getLatestDayjs = (dates) => {
    return exports.sDayJs.unix(Math.max(...dates.map((date) => date.unix())));
};
exports.getLatestDayjs = getLatestDayjs;
/**
 * Compute which 1-indexed week a date falls in relative to a start date.
 *
 * Week 1 = days 0-6, Week 2 = days 7-13, Week 3 = days 14-20, etc.
 */
const getOneIndexedWeek = (startDate, endDate) => {
    return Math.floor((0, exports.sDayJs)(endDate).diff((0, exports.sDayJs)(startDate), 'week', true)) + 1;
};
exports.getOneIndexedWeek = getOneIndexedWeek;
/**
 * Compute the 1-indexed current week within a fixed-length observation window,
 * clamped to [1, windowWeeks]. The single source of truth for every "Week N
 * of M" surface in the weekly report — Beat 3 carousel chip, ActiveTry LLM
 * prompt, ProductDetailData.weeksElapsed, anywhere week numbering renders.
 *
 *   - Day 0 / negative drift → 1 (just added)
 *   - Past N full weeks → N+1 (you're in your (N+1)-th week)
 *   - Beyond the window → windowWeeks (never reads "Week 9 of 8")
 *
 * Without a single util the carousel, LLM prose, and detail screen drift
 * apart by one week mid-window (round vs ceil vs floor produce different
 * answers between days 7 and 49). Always call this; never roll your own.
 */
const getCurrentWeekInWindow = (startDate, endDate, windowWeeks) => {
    const week = (0, exports.getOneIndexedWeek)(startDate, endDate);
    if (week < 1)
        return 1;
    if (week > windowWeeks)
        return windowWeeks;
    return week;
};
exports.getCurrentWeekInWindow = getCurrentWeekInWindow;
//# sourceMappingURL=dayjs.js.map